import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Drawer, Tooltip, Snackbar, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';
import { updateLicense, fetchCustomers, fetchLicenseRenewals, fetchServers, fetchLogs } from '../api/admin';
import { RenewLicenseDialog } from './RenewLicenseDialog';

interface LicenseDetailsDrawerProps {
    open: boolean;
    onClose: () => void;
    license: any;
    onLicenseUpdated?: () => void;
}

export function LicenseDetailsDrawer({ open, onClose, license, onLicenseUpdated }: LicenseDetailsDrawerProps) {
    const queryClient = useQueryClient();
    const [selectedServer, setSelectedServer] = useState<any>(null);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');

    // Admin Confirmation Dialogs
    const [suspendDialogOpen, setSuspendDialogOpen] = useState(false);
    const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
    const [renewDialogOpen, setRenewDialogOpen] = useState(false);

    const showMessage = (msg: string) => {
        setSnackbarMessage(msg);
        setSnackbarOpen(true);
    };

    // Queries
    const { data: customersData } = useQuery({
        queryKey: ['customers'],
        queryFn: fetchCustomers,
        enabled: open
    });
    const customers = customersData?.customers || [];

    const { data: serversData } = useQuery({
        queryKey: ['servers'],
        queryFn: fetchServers,
        enabled: open
    });
    const servers = serversData?.servers || [];

    const { data: renewalsData } = useQuery({
        queryKey: ['renewals', license?.licenseKey],
        queryFn: () => fetchLicenseRenewals(license.licenseKey),
        enabled: open && !!license,
    });
    const renewalsHistory = renewalsData?.renewals || [];

    const { data: logsData } = useQuery({
        queryKey: ['logs', license?.licenseKey],
        queryFn: () => fetchLogs({ licenseKey: license.licenseKey }),
        enabled: open && !!license,
    });
    const licenseLogs = logsData?.logs || [];

    const serverLogs = (logsData?.logs || []).filter((l: any) => l.serverId === selectedServer?.serverId);

    // Mutations
    const updateMutation = useMutation({
        mutationFn: ({ licenseKey, data }: { licenseKey: string; data: any }) => updateLicense(licenseKey, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['licenses'] });
            queryClient.invalidateQueries({ queryKey: ['customers'] });
            if (onLicenseUpdated) onLicenseUpdated();
            showMessage('License updated successfully');
        },
        onError: (err: any) => {
            showMessage(err.response?.data?.error || 'Operation failed');
        }
    });

    const handleUpdateStatus = async (status: 'active' | 'suspended' | 'revoked') => {
        if (!license) return;
        await updateMutation.mutateAsync({ licenseKey: license.licenseKey, data: { status } });
    };

    const handleUpdateLimit = async (limit: number) => {
        if (!license) return;
        await updateMutation.mutateAsync({ licenseKey: license.licenseKey, data: { serverLimit: limit } });
    };

    const getLinkedServerCounters = () => {
        if (!license) return { active: 0, blocked: 0, suspicious: 0, migrated: 0 };
        const linked = servers.filter((s: any) => s.licenseKey === license.licenseKey);
        return {
            active: linked.filter((s: any) => s.status === 'active').length,
            blocked: linked.filter((s: any) => s.status === 'blocked').length,
            suspicious: linked.filter((s: any) => s.status === 'suspicious').length,
            migrated: linked.filter((s: any) => s.status === 'migrated').length,
        };
    };

    const getTimelineLogStyle = (log: any) => {
        if (log.type === 'LICENSE_RENEWED') {
            return {
                bg: 'bg-green-500',
                text: 'text-green-700 dark:text-green-300',
                border: 'border-green-300 dark:border-green-700',
                icon: '🔄'
            };
        }
        if (log.type === 'LICENSE_UPDATE') {
            if (log.status === 'suspended') {
                return {
                    bg: 'bg-orange-500',
                    text: 'text-orange-700 dark:text-orange-300',
                    border: 'border-orange-300 dark:border-orange-700',
                    icon: '⏸️'
                };
            }
            if (log.status === 'revoked') {
                return {
                    bg: 'bg-red-500',
                    text: 'text-red-700 dark:text-red-300',
                    border: 'border-red-300 dark:border-red-700',
                    icon: '🚫'
                };
            }
            return {
                bg: 'bg-green-500',
                text: 'text-green-700 dark:text-green-300',
                border: 'border-green-300 dark:border-green-700',
                icon: '▶️'
            };
        }
        if (log.type === 'SERVER_BLOCK') {
            return {
                bg: 'bg-red-500',
                text: 'text-red-700 dark:text-red-300',
                border: 'border-red-300 dark:border-red-700',
                icon: '🔒'
            };
        }
        if (log.type === 'SERVER_MIGRATED') {
            return {
                bg: 'bg-blue-500',
                text: 'text-blue-700 dark:text-blue-300',
                border: 'border-blue-300 dark:border-blue-700',
                icon: '✈️'
            };
        }
        return {
            bg: 'bg-gray-400',
            text: 'text-gray-700 dark:text-gray-300',
            border: 'border-gray-300 dark:border-gray-700',
            icon: '📝'
        };
    };

    const formatFeatureKey = (key: string) => {
        if (key.toLowerCase() === 'drm') return 'DRM';
        if (key.toLowerCase() === 'vod') return 'VOD';
        return key
            .split(/[_-]/)
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    };

    if (!license) return null;

    const linkedServers = servers.filter((s: any) => s.licenseKey === license.licenseKey);
    const serverCounts = getLinkedServerCounters();

    return (
        <>
            <Drawer
                anchor="right"
                open={open}
                onClose={onClose}
                PaperProps={{
                    className: 'w-[500px] max-w-full p-6 dark:bg-gray-800 dark:text-white border-l border-gray-200 dark:border-gray-700 flex flex-col justify-between z-[1200]'
                }}
            >
                <div className="space-y-6 flex-grow overflow-y-auto pr-1">
                    <div className="flex justify-between items-center border-b pb-4 border-gray-200 dark:border-gray-700">
                        <div>
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">License Details</h2>
                            <p className="text-xs text-gray-500">{license.licenseKey}</p>
                        </div>
                        <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 cursor-pointer">
                            <CloseIcon />
                        </button>
                    </div>

                    {/* License Summary */}
                    <div className="grid grid-cols-2 gap-4 text-xs">
                        <div className="col-span-2">
                            <p className="text-[10px] text-gray-400 font-medium">License Key</p>
                            <div className="flex items-center gap-2 font-mono text-sm text-gray-800 dark:text-gray-200 font-semibold">
                                <span>{license.licenseKey}</span>
                                <Tooltip title="Copy Key" arrow>
                                    <button
                                        onClick={() => {
                                            navigator.clipboard.writeText(license.licenseKey);
                                            showMessage('License key copied');
                                        }}
                                        className="p-1 hover:bg-slate-200 dark:hover:bg-slate-800 rounded text-slate-500 cursor-pointer"
                                    >
                                        <ContentCopyIcon className="!w-3 !h-3" />
                                    </button>
                                </Tooltip>
                            </div>
                        </div>
                        <div>
                            <p className="text-[10px] text-gray-400 font-medium">Product ID</p>
                            <p className="font-semibold text-gray-850 dark:text-gray-200">{license.productId}</p>
                        </div>
                        <div>
                            <p className="text-[10px] text-gray-400 font-medium">Status</p>
                            <span className={`inline-block px-2 py-0.5 mt-1 text-[10px] rounded-full ${
                                license.status === 'active' 
                                    ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200'
                                    : license.status === 'suspended'
                                        ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200'
                                        : 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200'
                            }`}>
                                {license.status}
                            </span>
                        </div>
                        <div>
                            <p className="text-[10px] text-gray-400 font-medium">Customer ID</p>
                            <p className="text-gray-800 dark:text-gray-200 font-medium">
                                {license.customerId ? (
                                    customers.find((c: any) => c.customerId === license.customerId)?.companyName || license.customerId
                                ) : 'None'}
                            </p>
                        </div>
                        <div>
                            <p className="text-[10px] text-gray-400 font-medium">Expiration Date</p>
                            <p className="text-gray-800 dark:text-gray-200 font-mono font-medium">
                                {license.expiresAt ? new Date(license.expiresAt).toLocaleDateString() : 'Never'}
                            </p>
                        </div>
                        <div>
                            <p className="text-[10px] text-gray-400 font-medium">Created Date</p>
                            <p className="text-gray-800 dark:text-gray-200 font-mono">
                                {license.createdAt ? new Date(license.createdAt).toLocaleDateString() : 'N/A'}
                            </p>
                        </div>
                        <div>
                            <p className="text-[10px] text-gray-400 font-medium">Last Renewal Date</p>
                            <p className="text-gray-800 dark:text-gray-200 font-mono">
                                {license.lastRenewedAt ? new Date(license.lastRenewedAt).toLocaleString() : 'Never'}
                            </p>
                        </div>
                        <div className="col-span-2">
                            <p className="text-[10px] text-gray-400 font-medium">Server Limit / Usage</p>
                            <p className="font-semibold text-gray-850 dark:text-gray-200 text-sm">
                                {license.activeServerCount || 0} / {license.serverLimit} machines used
                            </p>
                        </div>
                    </div>

                    {/* Machine Status Summary */}
                    <div className="border-t pt-4 border-gray-200 dark:border-gray-700">
                        <h3 className="text-sm font-semibold mb-3 text-gray-900 dark:text-white">Machine Status Summary</h3>
                        <div className="grid grid-cols-4 gap-2 text-center text-xs">
                            <div className="p-2 bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-lg">
                                <p className="font-bold text-green-700 dark:text-green-300 text-sm">{serverCounts.active}</p>
                                <p className="text-[9px] text-green-600 dark:text-green-400 font-medium">Active</p>
                            </div>
                            <div className="p-2 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-lg">
                                <p className="font-bold text-red-700 dark:text-red-300 text-sm">{serverCounts.blocked}</p>
                                <p className="text-[9px] text-red-600 dark:text-red-400 font-medium">Blocked</p>
                            </div>
                            <div className="p-2 bg-yellow-50 dark:bg-yellow-950/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                                <p className="font-bold text-yellow-700 dark:text-yellow-300 text-sm">{serverCounts.suspicious}</p>
                                <p className="text-[9px] text-yellow-600 dark:text-yellow-400 font-medium">Suspicious</p>
                            </div>
                            <div className="p-2 bg-slate-50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-800 rounded-lg">
                                <p className="font-bold text-slate-700 dark:text-slate-350 text-sm">{serverCounts.migrated}</p>
                                <p className="text-[9px] text-slate-600 dark:text-slate-400 font-medium">Migrated</p>
                            </div>
                        </div>
                    </div>

                    {/* Feature Flags */}
                    <div className="border-t pt-4 border-gray-200 dark:border-gray-700">
                        <h3 className="text-sm font-semibold mb-3 text-gray-900 dark:text-white">Feature Flags</h3>
                        {!license.features || Object.keys(license.features).length === 0 ? (
                            <p className="text-xs text-gray-400">No custom feature flags configured.</p>
                        ) : (
                            <div className="grid grid-cols-2 gap-3 text-xs bg-slate-50 dark:bg-slate-900/40 p-4 rounded-xl border dark:border-gray-700">
                                {Object.entries(license.features).map(([key, val]: any) => (
                                    <div key={key} className="flex justify-between items-center border-b pb-2 dark:border-gray-700/60 col-span-2 last:border-b-0 last:pb-0">
                                        <span className="text-gray-650 dark:text-gray-300 font-medium text-xs">
                                            {formatFeatureKey(key)}
                                        </span>
                                        <span>
                                            {typeof val === 'boolean' ? (
                                                val ? (
                                                    <span className="px-2 py-0.5 text-[10px] rounded bg-green-50 dark:bg-green-950/30 text-green-700 dark:text-green-400 font-bold">
                                                        Enabled
                                                    </span>
                                                ) : (
                                                    <span className="px-2 py-0.5 text-[10px] rounded bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 font-bold">
                                                        Disabled
                                                    </span>
                                                )
                                            ) : typeof val === 'number' ? (
                                                <span className="px-2 py-0.5 text-[10px] rounded bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-400 font-mono font-bold">
                                                    {val.toLocaleString()}
                                                </span>
                                            ) : (
                                                <span className="px-2 py-0.5 text-[10px] rounded bg-purple-50 dark:bg-purple-950/30 text-purple-700 dark:text-purple-350 font-semibold">
                                                    {String(val)}
                                                </span>
                                            )}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Machines Under License */}
                    <div className="border-t pt-4 border-gray-200 dark:border-gray-700">
                        <h3 className="text-sm font-semibold mb-3 text-gray-900 dark:text-white">Machines Under License</h3>
                        {linkedServers.length === 0 ? (
                            <p className="text-xs text-gray-400">No machines activated under this license.</p>
                        ) : (
                            <div className="overflow-x-auto border rounded-xl dark:border-gray-700 text-xs">
                                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                    <thead className="bg-slate-50 dark:bg-slate-900/30">
                                        <tr>
                                            <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase">Machine Name</th>
                                            <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                                            <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase">Version</th>
                                            <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase">Reacts</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                                        {linkedServers.map((srv: any) => (
                                            <tr 
                                                key={srv.serverId} 
                                                onClick={() => setSelectedServer(srv)}
                                                className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors"
                                            >
                                                <td className="px-3 py-2 font-medium text-gray-800 dark:text-gray-200">{srv.serverName}</td>
                                                <td className="px-3 py-2">
                                                    <span className={`px-1.5 py-0.5 rounded-full text-[9px] ${
                                                        srv.status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200' :
                                                        srv.status === 'blocked' ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200' :
                                                        srv.status === 'suspicious' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200' :
                                                        'bg-gray-100 text-gray-800 dark:bg-gray-700/40 dark:text-gray-200'
                                                    }`}>
                                                        {srv.status}
                                                    </span>
                                                </td>
                                                <td className="px-3 py-2 font-mono text-[10px] text-gray-650 dark:text-gray-300">{srv.version}</td>
                                                <td className="px-3 py-2 font-semibold text-gray-750 dark:text-gray-200">{srv.reactivationCount || 0}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    {/* Renewal History */}
                    <div className="border-t pt-4 border-gray-200 dark:border-gray-700">
                        <h3 className="text-sm font-semibold mb-3 text-gray-900 dark:text-white">Renewal History</h3>
                        {renewalsHistory.length === 0 ? (
                            <p className="text-xs text-gray-400">No renewals found in history.</p>
                        ) : (
                            <div className="space-y-3">
                                {renewalsHistory.map((ren: any) => (
                                    <div key={ren.id} className="p-3 bg-slate-50 dark:bg-slate-900/40 rounded-xl border dark:border-gray-700 text-xs">
                                        <div className="flex justify-between items-center font-semibold text-gray-800 dark:text-gray-200 mb-1">
                                            <span>Duration: {ren.duration === 'custom' ? 'Custom Expiry' : ren.duration.replace('_', ' ')}</span>
                                            <span className="text-[10px] text-gray-400">{new Date(ren.createdAt).toLocaleDateString()}</span>
                                        </div>
                                        <p className="text-[10px] text-gray-500 font-mono">
                                            {ren.previousExpiry ? new Date(ren.previousExpiry).toLocaleDateString() : 'None'} ➡️ {new Date(ren.newExpiry).toLocaleDateString()}
                                        </p>
                                        <p className="text-[10px] text-gray-505 mt-1">Admin: <span className="font-semibold text-gray-650 dark:text-gray-300">{ren.admin}</span></p>
                                        {ren.notes && <p className="text-[10px] italic text-gray-600 dark:text-gray-400 mt-1 bg-white dark:bg-slate-900/80 p-1.5 rounded">"{ren.notes}"</p>}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Audit Timeline */}
                    <div className="border-t pt-4 border-gray-200 dark:border-gray-700">
                        <h3 className="text-sm font-semibold mb-3 text-gray-900 dark:text-white">Audit Timeline</h3>
                        <div className="space-y-3 max-h-[220px] overflow-y-auto pr-1 text-xs">
                            {licenseLogs.length === 0 ? (
                                <p className="text-gray-400">No logs for this license.</p>
                            ) : (
                                licenseLogs.map((log: any) => {
                                    const timelineStyle = getTimelineLogStyle(log);
                                    return (
                                        <div key={log.id} className="flex gap-2 border-l border-gray-300 dark:border-gray-700 pl-4 relative">
                                            <div className={`w-5 h-5 rounded-full ${timelineStyle.bg} border-2 ${timelineStyle.border} absolute -left-[11px] top-0.5 flex items-center justify-center text-[10px] shadow`}>
                                                {timelineStyle.icon}
                                            </div>
                                            <div className="flex-grow pl-2">
                                                <p className={`font-semibold ${timelineStyle.text}`}>{log.type}</p>
                                                <p className="text-[10px] text-gray-400">{log.details || 'Action completed'}</p>
                                                <p className="text-[10px] text-gray-505 font-mono mt-0.5">{new Date(log.createdAt || log.timestamp).toLocaleString()}</p>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>

                {/* Drawer Footer Actions */}
                <div className="border-t pt-4 mt-4 border-gray-200 dark:border-gray-700 flex flex-wrap gap-2">
                    {license.status === 'active' ? (
                        <button
                            onClick={() => setSuspendDialogOpen(true)}
                            className="px-3 py-1.5 bg-yellow-500 text-white rounded text-xs hover:bg-yellow-600 font-medium cursor-pointer"
                        >
                            Suspend License
                        </button>
                    ) : license.status === 'suspended' ? (
                        <button
                            onClick={() => handleUpdateStatus('active')}
                            className="px-3 py-1.5 bg-green-600 text-white rounded text-xs hover:bg-green-700 font-medium cursor-pointer"
                        >
                            Reactivate
                        </button>
                    ) : null}

                    {license.status !== 'revoked' && (
                        <button
                            onClick={() => setRenewDialogOpen(true)}
                            className="px-3 py-1.5 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 font-medium cursor-pointer"
                        >
                            Renew License
                        </button>
                    )}

                    {license.status !== 'revoked' && (
                        <button
                            onClick={() => setRevokeDialogOpen(true)}
                            className="px-3 py-1.5 bg-red-650 text-white rounded text-xs hover:bg-red-750 font-medium cursor-pointer"
                        >
                            Revoke License
                        </button>
                    )}
                </div>
            </Drawer>

            {/* Nested Server Details Drawer */}
            <Drawer
                anchor="right"
                open={!!selectedServer}
                onClose={() => setSelectedServer(null)}
                PaperProps={{
                    className: 'w-[450px] max-w-full p-6 dark:bg-gray-800 dark:text-white border-l border-gray-200 dark:border-gray-700 flex flex-col justify-between z-[2000]'
                }}
            >
                {selectedServer && (
                    <div className="space-y-6 flex-grow overflow-y-auto pr-1">
                        <div className="flex justify-between items-center border-b pb-4 border-gray-200 dark:border-gray-700">
                            <div>
                                <h2 className="text-xl font-bold text-gray-900 dark:text-white">Machine Details</h2>
                                <p className="text-xs text-gray-500">{selectedServer.serverId}</p>
                            </div>
                            <button onClick={() => setSelectedServer(null)} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 cursor-pointer">
                                <CloseIcon />
                            </button>
                        </div>

                        <div className="grid grid-cols-2 gap-4 text-xs">
                            <div>
                                <p className="text-[10px] text-gray-400 font-medium">Machine Name</p>
                                <p className="font-semibold text-gray-800 dark:text-gray-200">{selectedServer.serverName}</p>
                            </div>
                            <div>
                                <p className="text-[10px] text-gray-400 font-medium">Status</p>
                                <span className={`inline-block px-2 py-0.5 mt-1 text-[10px] rounded-full ${
                                    selectedServer.status === 'active' 
                                        ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200'
                                        : selectedServer.status === 'suspicious'
                                            ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200'
                                            : selectedServer.status === 'blocked'
                                                ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200'
                                                : 'bg-gray-100 text-gray-800 dark:bg-gray-700/40 dark:text-gray-200'
                                }`}>
                                    {selectedServer.status}
                                </span>
                            </div>
                            <div className="col-span-2">
                                <p className="text-[10px] text-gray-400 font-medium">License Key</p>
                                <p className="font-mono text-gray-800 dark:text-gray-200">{selectedServer.licenseKey}</p>
                            </div>
                            <div className="col-span-2">
                                <p className="text-[10px] text-gray-400 font-medium">Fingerprint</p>
                                <div className="flex items-center gap-2 font-mono text-xs text-gray-700 dark:text-gray-300 bg-slate-50 dark:bg-slate-900/40 p-2 rounded border dark:border-gray-700">
                                    <span className="break-all">{selectedServer.fingerprint}</span>
                                    <button
                                        onClick={() => {
                                            navigator.clipboard.writeText(selectedServer.fingerprint);
                                            showMessage('Fingerprint copied');
                                        }}
                                        className="p-1 hover:bg-slate-200 dark:hover:bg-slate-800 rounded text-slate-500 cursor-pointer"
                                        title="Copy fingerprint"
                                    >
                                        <ContentCopyIcon className="!w-3.5 !h-3.5" />
                                    </button>
                                </div>
                            </div>
                            <div>
                                <p className="text-[10px] text-gray-400 font-medium">IP Address</p>
                                <p className="text-gray-800 dark:text-gray-200">{selectedServer.ipAddress || 'N/A'}</p>
                            </div>
                            <div>
                                <p className="text-[10px] text-gray-400 font-medium">Version</p>
                                <p className="text-gray-800 dark:text-gray-200 font-mono">{selectedServer.version}</p>
                            </div>
                            <div>
                                <p className="text-[10px] text-gray-400 font-medium">Reactivations</p>
                                <p className="text-gray-800 dark:text-gray-200 font-semibold">{selectedServer.reactivationCount || 0}</p>
                            </div>
                            <div>
                                <p className="text-[10px] text-gray-400 font-medium">Activated Date</p>
                                <p className="text-gray-800 dark:text-gray-200">
                                    {selectedServer.activatedAt ? new Date(selectedServer.activatedAt).toLocaleDateString() : 'N/A'}
                                </p>
                            </div>
                        </div>

                        <div className="border-t pt-4 border-gray-200 dark:border-gray-700">
                            <h3 className="text-sm font-semibold mb-3 text-gray-900 dark:text-white">Audit Timeline</h3>
                            <div className="space-y-3 max-h-[200px] overflow-y-auto pr-1 text-xs">
                                {serverLogs.length === 0 ? (
                                    <p className="text-gray-400">No logs for this machine.</p>
                                ) : (
                                    serverLogs.map((log: any) => (
                                        <div key={log.id} className="flex gap-2 border-l border-gray-300 dark:border-gray-700 pl-3 relative">
                                            <div className="w-1.5 h-1.5 rounded-full bg-blue-500 absolute -left-[4px] top-1"></div>
                                            <div className="flex-grow">
                                                <p className="font-semibold text-gray-700 dark:text-gray-300">{log.type}</p>
                                                <p className="text-[10px] text-gray-400">{log.details || 'No additional details'}</p>
                                                <p className="text-[10px] text-gray-400">{new Date(log.createdAt || log.timestamp).toLocaleString()}</p>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </Drawer>

            {/* Suspend Confirmation Dialog */}
            <Dialog
                open={suspendDialogOpen}
                onClose={() => setSuspendDialogOpen(false)}
            >
                <DialogTitle>Suspend License</DialogTitle>
                <DialogContent>
                    <DialogContentText className="!mb-2">
                        Are you sure you want to suspend this license?
                    </DialogContentText>
                    <DialogContentText>
                        All associated machines will stop validating until the license is resumed.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSuspendDialogOpen(false)}>Cancel</Button>
                    <Button 
                        onClick={() => {
                            handleUpdateStatus('suspended');
                            setSuspendDialogOpen(false);
                        }} 
                        color="warning" 
                        autoFocus
                    >
                        Suspend License
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Revoke Confirmation Dialog */}
            <Dialog
                open={revokeDialogOpen}
                onClose={() => setRevokeDialogOpen(false)}
            >
                <DialogTitle className="!text-red-650 dark:!text-red-400 font-bold">⚠️ Revoke License</DialogTitle>
                <DialogContent>
                    <DialogContentText className="!mb-3 !text-gray-900 dark:!text-gray-100 font-medium">
                        Are you sure you want to revoke this license?
                    </DialogContentText>
                    <DialogContentText className="!text-red-500 font-semibold">
                        This action cannot be easily undone and all associated machines will permanently stop validating.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRevokeDialogOpen(false)}>Cancel</Button>
                    <Button 
                        onClick={() => {
                            handleUpdateStatus('revoked');
                            setRevokeDialogOpen(false);
                        }} 
                        className="!bg-red-600 !text-white hover:!bg-red-700"
                        autoFocus
                    >
                        Revoke License
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Shared Renew Dialog inside the details drawer */}
            <RenewLicenseDialog
                open={renewDialogOpen}
                onClose={() => setRenewDialogOpen(false)}
                license={license}
                onSuccess={() => {
                    queryClient.invalidateQueries({ queryKey: ['licenses'] });
                    queryClient.invalidateQueries({ queryKey: ['logs'] });
                    queryClient.invalidateQueries({ queryKey: ['renewals'] });
                    if (onLicenseUpdated) onLicenseUpdated();
                    setRenewDialogOpen(false);
                }}
            />

            {/* Notifications inside Drawer */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={() => setSnackbarOpen(false)}
                message={snackbarMessage}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            />
        </>
    );
}
export default LicenseDetailsDrawer;
